<script lang="ts">
    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import { gettext as _, resolve } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as ugroups } from 'hydria/services/session'

    import { groups } from 'admin/permissions'

    import type { AdminUser, UserStatus } from './user'
    import { SvelteSet } from 'svelte/reactivity'

    interface Props {
        user: AdminUser
        status: UserStatus
        setLockedState: (nv: boolean) => void
        userImportData: (udata: any) => void
    }

    let { user, status, setLockedState, userImportData }: Props = $props()

    let userGroups: SvelteSet<string> = $derived(new SvelteSet(status == 'ready' ? user.groups : []))

    let definedPerms: SvelteSet<string> = $derived.by(() => {
        if ($groups && user && user.groups) {
            let perms: SvelteSet<string> = new SvelteSet()
            for (const group of $groups) {
                perms = new SvelteSet([...perms, ...group.permissions.map((g) => g.id)])
            }
            return perms
        }
        return new SvelteSet()
    })

    let unknownPerms = $derived(definedPerms && user && user.groups ? new SvelteSet(user.groups.filter((g) => !definedPerms.has(g) && g != 'active')) : new SvelteSet())

    let unlocking = $state(false)
    async function unlockUser() {
        unlocking = true

        try {
            await simpleFetch('DELETE', `/json/users/1/${user.id}/password-lock`)
            notify({
                type: 'success',
                title: $_('Unlock user account'),
                text: $_('The account is now unlocked, email sent.'),
            })
            setLockedState(false)
            // user.update((user) => {
            //     user.locked = false
            //     return user
            // })
        } catch (e: any) {
            console.error('Failed to unlock:', e)
            notify({
                type: 'danger',
                title: $_('Unlock user account'),
                text: 'The account could not be unlocked' + (e && e.data && e.data.error ? ` : ${e.data.error}` : '.'),
            })
        }

        unlocking = false
    }

    let recovering = $state(false)
    async function lostPassword() {
        recovering = true

        try {
            const r = await simpleFetch('PUT', `/json/users/1/${user.id}/send-password-email`)
            notify({
                type: 'success',
                title: $_('Password'),
                text: $_('Le courriel est maintenant en route, contenant un lien permettant de changer le mot de passe.'),
            })
        } catch (e: any) {
            console.error('Failed to send password:', e)
            notify({
                type: 'danger',
                title: $_('Password'),
                text: 'Un email n’a pas pu être envoyé suite à une erreur' + (e && e.data && e.data.error ? ` : ${e.data.error}` : '.'),
            })
        }

        recovering = false
    }

    let cleaningUp = $state(false)
    async function cleanupPermissions() {
        cleaningUp = true

        const ng = [...userGroups].filter((g) => !unknownPerms.has(g))

        try {
            const r = await simpleFetch('PUT', `/json/users/1/${user.id}/capabilities`, { groups: ng })
            userImportData(r.data)
        } catch (e) {
            console.error('Failed to clean capabilities:', e)
        }

        cleaningUp = false
    }

    let inEdition = $state(false)

    let eUserGroups: SvelteSet<string> = $derived(new SvelteSet([...userGroups]))
    let eChanged = $derived(!isEqual(userGroups, eUserGroups))

    let savingPerms = $state(false)
    async function savePermissions(toggleActive: boolean = false) {
        savingPerms = true

        const ng = [...eUserGroups]
        if ((user.active && !toggleActive) || (toggleActive && !user.active)) ng.push('active')

        try {
            const r = await simpleFetch('PUT', `/json/users/1/${user.id}/capabilities`, { groups: ng })
            userImportData(r.data)
        } catch (e) {
            console.error('Failed to save capabilities:', e)
        }

        savingPerms = false
        inEdition = false
    }

    function togglePerm(perm: string) {
        if (!inEdition) return

        if (eUserGroups.has(perm)) eUserGroups.delete(perm)
        else eUserGroups.add(perm)
        eUserGroups = eUserGroups
    }
</script>

<div class="card flex-fill">
    <div class="card-header text-bg-primary">{$_('Capabilities')}</div>

    <div class="card-body">
        <strong>{$_('User status')}</strong>
        {#if status == 'loading'}
            <LoadingPattern pattern={['x', 'x']} />
        {:else}
            <ul class="list-unstyled">
                <li class:text-success={user.active} class:text-warning={!user.active}>
                    {#if user.active}
                        <Icon inline icon="mdi:checkbox-marked" />
                        {$_('The user is active and can sign in')}
                    {:else}
                        <Icon inline icon="mdi:checkbox-blank" /> {$_('The user is active and can sign in')}
                    {/if}
                    <button type="button" class="btn btn-sm btn-outline-secondary" disabled={savingPerms || cleaningUp} onclick={() => savePermissions(true)}>{$_('Toggle')}</button>
                </li>
                {#if user.has_password}
                    <li class="text-success"><Icon inline icon="mdi:checkbox-marked" /> {$_('The user has a password')}</li>
                {:else}
                    <li class="text-warning"><Icon inline icon="mdi:checkbox-blank" /> {$_('The user does not have a password')}</li>
                {/if}
                {#if user.locked}
                    <li class="text-danger"><Icon inline icon="mdi:lock-alert-outline" /> {$_('The password is locked (too many failed sign in attempts)')}</li>
                    <li>
                        {#if unlocking}
                            <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                            {$_('Working…')}
                        {:else}
                            <button class="btn btn-link p-0" type="button" onclick={() => unlockUser()}>{$_('Unlock the password (will send an email)')}</button>
                        {/if}
                    </li>
                {/if}

                <li>
                    {#if recovering}
                        <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                        {$_('Working…')}
                    {:else}
                        <button class="btn btn-link p-0" type="button" onclick={() => lostPassword()}>{$_('Send password reset email')}</button>
                    {/if}
                </li>
            </ul>

            <dl style="margin-top: 0; padding-top: 0; {inEdition ? 'border: 1px dashed red; padding: 1em;' : ''}">
                {#each $groups as group}
                    <dt class="capabilities-types">{$resolve(group.name)}</dt>
                    <dd>
                        <ul class="list-unstyled" style="margin-bottom: 0">
                            {#each group.permissions as perm}
                                {#if perm.id != 'dev' || $ugroups.has('dev') || !inEdition}
                                    <li aria-hidden="true" class:text-muted={!eUserGroups.has(perm.id)} class:text-success={eUserGroups.has(perm.id) && perm.level == 1} class:text-info={eUserGroups.has(perm.id) && perm.level == 2} class:text-warning={eUserGroups.has(perm.id) && perm.level == 3} class:text-danger={eUserGroups.has(perm.id) && perm.level == 4} style={inEdition ? 'cursor: pointer' : ''} onclick={() => togglePerm(perm.id)}>
                                        <Icon inline icon={eUserGroups.has(perm.id) ? 'mdi:checkbox-marked' : 'mdi:checkbox-blank'} />
                                        {$resolve(perm.name)}
                                    </li>
                                {/if}
                            {/each}
                        </ul>
                    </dd>
                {/each}

                {#if unknownPerms.size}
                    <dt class="capabilities-types"><em>Permissions inconnues</em></dt>
                    <dd>
                        <ul class="list-unstyled" style="margin-bottom: 0">
                            {#each [...unknownPerms] as perm}
                                <li><Icon inline icon="mdi:checkbox-marked" /> {perm}</li>
                            {/each}
                        </ul>
                    </dd>
                {/if}
            </dl>
        {/if}
    </div>
    <div class="card-footer">
        {#if status == 'ready'}
            {#if inEdition}
                <button type="button" class="btn btn-outline-primary" disabled={savingPerms} onclick={() => (inEdition = false)}>Cancel</button>
                <button type="button" class="btn btn-outline-primary" disabled={!eChanged || savingPerms} onclick={() => savePermissions()}>
                    {#if savingPerms}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        Saving…
                    {:else}
                        Save
                    {/if}
                </button>
            {:else}
                {#if unknownPerms.size}
                    <button type="button" class="btn btn-outline-primary" disabled={cleaningUp || savingPerms} onclick={() => cleanupPermissions()}>
                        {#if cleaningUp}
                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {$_('Working…')}
                        {:else}
                            {$_('Remove unknown permissions')}
                        {/if}
                    </button>
                {/if}

                <button type="button" class="btn btn-outline-primary" disabled={cleaningUp || savingPerms} onclick={() => (inEdition = true)}>
                    {#if savingPerms}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {$_('Working…')}
                    {:else}
                        {$_('Edit permissions')}
                    {/if}
                </button>
            {/if}
        {/if}
    </div>
</div>
